/**
 * GridInitNode — defines the simulation grid and its layers.
 *
 * Base params: cols, rows.
 * Custom params are layers — each accepts a number[][] from generators
 * or uses its scalar value as a fill. Outputs a GridState on any change.
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@/graph/Payload';
import { params, internals } from '@/nodes/TypedEndpoints';
import { createGridState, emptyLayer } from '../GridState';
import { logger } from '@/logging';

const endpoints = {
  ...params({
    cols: { value: 20, min: 1, max: 128, step: 1, info: 'Grid columns' },
    rows: { value: 20, min: 1, max: 128, step: 1, info: 'Grid rows' },
  }),
  ...internals({
    out: { dataType: 'any' as any, isOutput: true, label: 'Out' },
  }),
};

class GridInitNode extends BaseNode<typeof endpoints> {
  initialize(): void {
    super.initialize();
    this.buildAndPush();
  }

  onParamChanged(_payload: Payload, _endpointId: string): void {
    this.buildAndPush();
  }

  onInternalChanged(_payload: Payload, _endpointId: string): void {
    this.buildAndPush();
  }

  private buildAndPush(): void {
    const cols = this.params.cols.value;
    const rows = this.params.rows.value;
    const customParams = this.deps.endpointApi.getCustomParams();
    const layers: Record<string, number[][]> = {};

    logger.trace('GridInit', `buildAndPush cols=${cols} rows=${rows} customParams=${Object.keys(customParams).join(',')}`);

    for (const epId of Object.keys(customParams)) {
      const val = this.deps.endpointApi.getValue(epId);
      if (Array.isArray(val) && Array.isArray(val[0])) {
        // number[][] from a generator — use directly (or trim/pad to fit)
        layers[epId] = val as number[][];
        logger.trace('GridInit', `layer ${epId}: array ${val.length}x${val[0].length}`);
      } else {
        // Scalar fill
        const fill = typeof val === 'number' ? val : 0;
        layers[epId] = emptyLayer(cols, rows, fill);
        logger.trace('GridInit', `layer ${epId}: fill ${fill}`);
      }
    }

    const state = createGridState(cols, rows, layers);
    logger.trace('GridInit', `pushing GridState with ${Object.keys(state.layers).length} layers: ${Object.keys(state.layers).join(',')}`);
    this.internals.out.set(state);
  }
}

export const gridInitNodeDef: NodeDef<GridInitNode> = {
  type: 'GridInit',
  shortName: 'gridinit',
  label: 'Grid Init',
  info: 'Defines the simulation grid. Add layers as custom params. Connect generators to layer inputs, then connect out to GridBuffer.init.',
  domain: Domain.Data,
  endpoints,
  allowAddParam: true,
  ctor: GridInitNode,
};
